import { initAuth, logout } from "@/auth/keycloak"
import { createCustomer, getClickId } from "@/api/tapfiliate.api"
import store from "./store/store"
import { trackGtmEvent } from "@/services/GtmService"
import { identifyPostHogUser, initPostHog } from "@/services/PostHogService"
import { showBootError } from "@/bootError"
import { STORAGE_KEYS } from "@/auth/session"
import { registerSessionTeardown } from "@/sessionTeardown"

// Armed before Keycloak so a session ending during boot — an auth error, or the
// Sign out on the boot error screen — tears down as completely as a normal one.
registerSessionTeardown()

// Init before Keycloak so cross-domain bootstrap IDs from zeno.fm are applied
// before any post-login events fire.
initPostHog()

// Tapfiliate referral tracking — best-effort. Wrapped so a referral/Tapfiliate
// failure can never block app boot.
const trackReferral = async (user) => {
  try {
    const referralCode = localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE)

    if (!referralCode) {
      return
    }

    const clickId = await getClickId(referralCode)

    if (!clickId) {
      return
    }

    await createCustomer(clickId, user.email, {
      id: user.id,
      email: user.email,
      first_name: user.basic_info.first_name,
      last_name: user.basic_info.last_name,
    })

    localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE)
  } catch (e) {
    console.error("[BOOT] Referral tracking failed", e)
  }
}

// App boot, run once Keycloak has initialized. Mounts the app only after the
// user is loaded; a mid-session 401 is recovered by the toolsClient interceptor.
const onReady = async (authenticated) => {
  if (!authenticated) {
    await logout()
    return
  }

  try {
    const user = await store.dispatch("Profile/fetchUser", {}, { root: true })

    trackGtmEvent({
      event: "user_authenticated",
      user_id: user.id,
    })

    // Link Keycloak user to the PostHog person (flag assignment / conversion join).
    // No other PostHog product events here — experiment only captures plan CTA + paid plan.
    identifyPostHogUser(user)

    await trackReferral(user)
  } catch (e) {
    // Vue is never mounted on this path, and #loading-bg is only torn down by
    // the router's afterEach — so without this the user is left watching the
    // spinner forever, with no message and nothing to click.
    console.error("[BOOT] Failed to load user", e)

    showBootError({
      title: "We couldn't load your account",
      message: "You're signed in, but the Tools service didn't respond. This is usually temporary.",
      error: e,
    })

    return
  }

  try {
    // ./bootstrap registers global components and mounts the Vue app.
    require("./bootstrap")
  } catch (e) {
    // keycloak-js invokes onReady without awaiting it, so throwing here would
    // surface only as an unhandled rejection and leave the spinner running.
    console.error("[BOOT] Failed to start the app", e)

    showBootError({
      title: "We couldn't start the app",
      message: "Something went wrong while loading it. Reloading usually clears this.",
      error: e,
    })
  }
}

initAuth(onReady).catch((e) => {
  console.error("[BOOT] Keycloak init failed", e)

  showBootError({
    title: "We couldn't reach sign-in",
    message: "The authentication service didn't respond. This is usually temporary.",
    error: e,
  })
})
